import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { imageUrl } from '../lib/images'
import { getLanguageFromPath } from '../i18n/routes'
import LanguageSwitcher from './LanguageSwitcher'

const HEADER_TEXTS = {
  da: {
    homeUrl: '/',
    retailers: 'Forhandlerliste',
    retailersUrl: '/forhandlerliste',
    contact: 'Find mig',
    contactUrl: '/aabningstider-og-adresse',
    about: 'Om mig',
    aboutUrl: '/om-mig',
    meetKarina: 'Mød Karina',
    euLag: 'EU LAG',
    euLagUrl: '/om-mig#projektstoette',
    cakeInquiry: 'Kageforespørgsel',
    cakeInquiryUrl: '/forspoergsel',
    shopUrl: '/webshop',
    shopTitle: 'Se udvalg i webshoppen',
    sendInquiry: 'Send Forespørgsel',
    visitTitle: 'Besøg butik & café:',
  },
  en: {
    homeUrl: '/en',
    retailers: 'Retailers',
    retailersUrl: '/en/retailers',
    contact: 'Visit & Hours',
    contactUrl: '/en/opening-hours-and-location',
    about: 'About',
    aboutUrl: '/en/about-me',
    meetKarina: 'Meet Karina',
    euLag: 'EU LAG Support',
    euLagUrl: '/en/eu-lag-support',
    cakeInquiry: 'Cake Inquiry',
    cakeInquiryUrl: '/en/cake-inquiry',
    shopUrl: '/en/shop',
    shopTitle: 'Explore online shop',
    sendInquiry: 'Send Cake Inquiry',
    visitTitle: 'Visit boutique & café:',
  },
  de: {
    homeUrl: '/de',
    retailers: 'Händler',
    retailersUrl: '/de/haendler',
    contact: 'Besuch & Zeiten',
    contactUrl: '/de/oeffnungszeiten-und-adresse',
    about: 'Über mich',
    aboutUrl: '/de/ueber-mich',
    meetKarina: 'Karina kennenlernen',
    euLag: 'EU-LAG Förderung',
    euLagUrl: '/de/eu-lag-foerderung',
    cakeInquiry: 'Tortenanfrage',
    cakeInquiryUrl: '/de/tortenanfrage',
    shopUrl: '/de/shop',
    shopTitle: 'Onlineshop ansehen',
    sendInquiry: 'Tortenanfrage senden',
    visitTitle: 'Besuchen Sie Café & Manufaktur:',
  },
}

// Precomputed cupcake wave path for bottom shape divider
const CUPCAKE_WAVE_COUNT = 24
const CUPCAKE_WAVE_WIDTH = 50 // 24 * 50 = 1200
const cupcakeWaveSegments = Array.from({ length: CUPCAKE_WAVE_COUNT })
  .map((_, i) => {
    const x = i * CUPCAKE_WAVE_WIDTH
    return `C ${x + 13} 16, ${x + 37} 16, ${x + CUPCAKE_WAVE_WIDTH} 2`
  })
  .join(' ')

const cupcakeWaveFillPath = `M 0 0 L 0 2 ${cupcakeWaveSegments} L 1200 0 Z`
const cupcakeWaveStrokePath = `M 0 2 ${cupcakeWaveSegments}`

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()

  const currentLang = getLanguageFromPath(location.pathname)
  const t = HEADER_TEXTS[currentLang] || HEADER_TEXTS.da
  const navigationLabels = {
    da: { home: 'Forside', main: 'Hovednavigation', open: 'Åbn menu', close: 'Luk menu' },
    en: { home: 'Home', main: 'Main navigation', open: 'Open menu', close: 'Close menu' },
    de: { home: 'Startseite', main: 'Hauptnavigation', open: 'Menü öffnen', close: 'Menü schließen' },
  }[currentLang] || { home: 'Forside', main: 'Hovednavigation', open: 'Åbn menu', close: 'Luk menu' }

  // Close menus on navigation
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  // Scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled((previous) => window.scrollY > (previous ? 20 : 120))
    }
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navLinks = [
    { label: t.retailers, path: t.retailersUrl },
    { label: t.contact, path: t.contactUrl },
  ]

  const isOmMigActive =
    location.pathname === t.aboutUrl ||
    location.pathname === t.euLagUrl ||
    location.pathname === '/om-mig-2'

  return (
    <header
      className={`sticky top-0 z-40 transition-[background-color,box-shadow,padding] duration-300 backdrop-blur-md ${
        scrolled
          ? 'bg-[#FDF8F7]/88 shadow-sm py-1.5 md:py-2'
          : 'bg-[#FDF8F7]/78 py-3 md:py-4'
      }`}
    >
     <div className="min-[667px]:hidden border-b border-[#C68D94]/20">
       <div className={`max-w-7xl mx-auto px-4 flex justify-end py-1 bg-[#9e5d67] ${scrolled ? '-mt-1.5' : '-mt-3'}`}>
         <LanguageSwitcher />
       </div>
     </div>
     <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between max-[666px]:pt-2">
       {/* Brand Logo */}
        <Link
          to={t.homeUrl}
          className="flex items-center gap-3 group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A35D67] rounded-lg"
          aria-label={`Fanø Chokolade - ${navigationLabels.home}`}
        >
          <img
            src={imageUrl('/_assets/fanoe-chokolade-logo-lockup-color-rgb.png', 'width=600,quality=90,format=auto')}
            alt="Fanø Chokolade"
            className={`w-auto object-contain transition-[height] duration-300 ${scrolled ? 'h-16 sm:h-19 md:h-24' : 'h-22 sm:h-26 md:h-32'}`}
          />
        </Link>

        {/* Desktop Navigation: VISIBLE ONLY ON SCREENS >= 1200px */}
        <nav
          className="hidden min-[1200px]:flex items-center space-x-8"
          aria-label={navigationLabels.main}
        >
          {navLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              className={({ isActive }) =>
                `text-[13px] tracking-wider uppercase font-semibold transition-colors duration-200 relative py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A35D67] rounded ${
                  isActive
                    ? 'text-[#A35D67] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-[#A35D67] after:rounded-full'
                    : 'text-[#3E3435]/80 hover:text-[#A35D67]'
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}

          {/* Direct link to Om mig */}
          <Link
            to={t.aboutUrl}
            className={`text-[13px] tracking-wider uppercase font-semibold transition-colors duration-200 relative py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A35D67] rounded ${
              isOmMigActive
                ? 'text-[#A35D67] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-[#A35D67] after:rounded-full'
                : 'text-[#3E3435]/80 hover:text-[#A35D67]'
            }`}
          >
            {t.about}
          </Link>

          {/* Quick link to custom cakes & inquiries */}
          <Link
            to={t.cakeInquiryUrl}
            className="text-[13px] tracking-wider uppercase font-semibold text-[#3E3435]/80 hover:text-[#A35D67] transition-colors py-1"
          >
            {t.cakeInquiry}
          </Link>
        </nav>

        {/* Right CTA / Action bar (Desktop >= 1200px) */}
        <div className="hidden min-[1200px]:flex items-center gap-4">
          <Link
            to={t.shopUrl}
            className="w-10 h-10 flex items-center justify-center text-[#3E3435] hover:text-[#A35D67] transition-colors"
            title={t.shopTitle}
            aria-label="Webshop"
          >
            <i className="fa-regular fa-bag-shopping text-base" aria-hidden="true" />
          </Link>

          {/* Language Switcher */}
          <LanguageSwitcher />
        </div>

        {/* Mobile / Tablet Action area: ACTIVATED UNDER 1200px */}
        <div className="flex min-[1200px]:hidden items-center gap-2 sm:gap-3">
          <div className="hidden min-[667px]:block"><LanguageSwitcher /></div>
          <Link
            to={t.shopUrl}
            className="w-9 h-9 flex items-center justify-center text-[#3E3435] hover:text-[#A35D67] transition-colors"
            aria-label={t.shopTitle}
          >
            <i className="fa-regular fa-bag-shopping text-base" aria-hidden="true" />
          </Link>
          <button
            type="button"
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            className="p-2 text-[#3E3435] hover:text-[#A35D67] focus:outline-none transition-colors"
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? navigationLabels.close : navigationLabels.open}
          >
            <i
              className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      {/* Mobile Drawer (visible under 1200px when opened) */}
      {createPortal(<div
        className={`min-[1200px]:hidden fixed inset-0 z-50 transition-all duration-300 ${
          mobileMenuOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />

        {/* Drawer panel */}
        <div
          className={`fixed top-0 right-0 bottom-0 w-full max-w-sm bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-out ${
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Drawer Header */}
          <div className="p-5 border-b border-[#C68D94]/20 flex items-center justify-between bg-[#FDF8F7]">
            <img
              src={imageUrl('/_assets/fanoe-chokolade-logo-lockup-color-rgb.png', 'width=320,quality=90,format=auto')}
              alt="Fanø Chokolade"
              className="h-20 w-auto object-contain"
            />
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="w-9 h-9 flex items-center justify-center text-[#3E3435] hover:text-[#A35D67] transition-colors"
              aria-label={navigationLabels.close}
            >
              <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
            </button>
          </div>

          {/* Drawer Links */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-2">
            {navLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-sm font-semibold tracking-wider transition-colors ${
                    isActive
                      ? 'bg-[#F7ECEE] text-[#A35D67]'
                      : 'text-[#3E3435] hover:bg-[#FDF8F7] hover:text-[#A35D67]'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}

            {/* Direct mobile link to Om mig */}
            <NavLink
              to={t.aboutUrl}
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `block px-4 py-3 rounded-xl text-sm font-semibold tracking-wider transition-colors ${
                  isActive ? 'bg-[#F7ECEE] text-[#A35D67]' : 'text-[#3E3435] hover:bg-[#FDF8F7] hover:text-[#A35D67]'
                }`
              }
            >
              {t.about}
            </NavLink>

            <div className="space-y-1">
              <NavLink
                to={t.cakeInquiryUrl}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-sm font-semibold tracking-wider transition-colors ${
                    isActive ? 'bg-[#F7ECEE] text-[#A35D67]' : 'text-[#3E3435] hover:bg-[#FDF8F7]'
                  }`
                }
              >
                {t.cakeInquiry}
              </NavLink>
            </div>

            {/* Language Switcher in Drawer */}
            <LanguageSwitcher isMobile onCloseMobile={() => setMobileMenuOpen(false)} />
          </div>

          {/* Drawer Footer */}
          <div className="p-6 bg-[#FDF8F7] border-t border-[#C68D94]/20 space-y-4">
            <div className="pt-2 text-xs text-[#3E3435]/80 space-y-1.5">
              <p className="font-semibold text-[#3E3435]">{t.visitTitle}</p>
              <p>Strandvejen 58, 6720 Fanø</p>
              <p>
                Tlf:{' '}
                <a
                  href="tel:60224704"
                  className="font-medium text-[#A35D67] whitespace-nowrap hover:underline"
                >
                  60 22 47 04
                </a>
              </p>
              <p>
                Email:{' '}
                <a
                  href="mailto:karina@fanoechokolade.dk"
                  className="font-medium text-[#A35D67] whitespace-nowrap hover:underline"
                >
                  karina@fanoechokolade.dk
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>, document.body)}

      {/* Cupcake Wave Shape Divider */}
      <div className="absolute left-0 right-0 top-full pointer-events-none overflow-hidden leading-none z-10">
        <svg
          viewBox="0 0 1200 20"
          preserveAspectRatio="none"
          className="w-full h-3 sm:h-4 md:h-5 block transition-all duration-300"
          style={{
            filter: 'drop-shadow(0 3px 3px rgba(198, 141, 148, 0.12))',
          }}
          aria-hidden="true"
        >
          <path
            d={cupcakeWaveFillPath}
            fill="#FDF8F7"
            fillOpacity={scrolled ? 0.88 : 0.78}
            className="transition-all duration-300"
          />
          <path
            d={cupcakeWaveStrokePath}
            stroke="#C68D94"
            strokeOpacity="0.3"
            strokeWidth="1.25"
            fill="none"
          />
        </svg>
      </div>
    </header>
  )
}
